import { useCallback, useEffect, useRef, useState } from "react";
import type { LimitesAnexo } from "../../../types/portalCliente";
import {
  arquivosDeTransferencia,
  colagemSemBinario,
  juntarAnexos,
} from "./anexos";
import { validarAnexos } from "./novoChamado";

/**
 * Anexos de um formulário, com as três entradas: o seletor de arquivos, colar
 * (Ctrl+V) e arrastar para qualquer ponto da janela. Use uma instância por tela — o ouvinte de
 * colagem é no documento, então dois formulários montados ao mesmo tempo
 * receberiam o mesmo arquivo.
 */
export const useAnexos = (limites: LimitesAnexo) => {
  const [anexos, setAnexos] = useState<File[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);

  // dragenter e dragleave também disparam ao passar por cima dos filhos; contar
  // as entradas evita o realce piscando enquanto o arquivo atravessa a tela.
  const profundidade = useRef(0);

  const adicionar = useCallback(
    (novos: File[]) => {
      if (novos.length === 0) return;
      setAnexos((atuais) => {
        const proximos = juntarAnexos(atuais, novos);
        setErro(validarAnexos(proximos, limites));
        return proximos;
      });
    },
    [limites]
  );

  const remover = useCallback(
    (indice: number) => {
      setAnexos((atuais) => {
        const proximos = atuais.filter((_, i) => i !== indice);
        setErro(validarAnexos(proximos, limites));
        return proximos;
      });
    },
    [limites]
  );

  const limpar = useCallback(() => {
    setAnexos([]);
    setErro(null);
  }, []);

  // Colar e arrastar valem para a janela inteira. Prender o arrasto a um
  // retângulo faria o arquivo solto dois centímetros ao lado ser aberto pelo
  // navegador — que troca a página e leva junto o formulário preenchido.
  useEffect(() => {
    const temArquivos = (dt: DataTransfer | null) =>
      Array.from(dt?.types ?? []).includes("Files");

    const aoColar = (e: ClipboardEvent) => {
      const dados = e.clipboardData;
      const arquivos = arquivosDeTransferencia(dados);

      if (arquivos.length === 0) {
        if (colagemSemBinario(dados)) {
          setErro(
            "Essa imagem foi copiada sem o arquivo. Salve-a e use “Escolher arquivos”, ou copie direto da ferramenta de captura."
          );
        }
        return;
      }

      // Só engolimos a colagem quando não veio texto junto: colar texto continua
      // caindo normalmente no campo em foco.
      if (!dados?.getData("text/plain")) e.preventDefault();
      adicionar(arquivos);
    };

    const aoEntrar = (e: globalThis.DragEvent) => {
      if (!temArquivos(e.dataTransfer)) return;
      // dragenter e dragleave também disparam ao passar por cima de cada filho;
      // contar as entradas evita o realce piscando enquanto o arquivo atravessa
      // a tela.
      profundidade.current += 1;
      setArrastando(true);
    };

    const aoPassar = (e: globalThis.DragEvent) => {
      // Sem preventDefault no dragover o navegador recusa o drop.
      if (temArquivos(e.dataTransfer)) e.preventDefault();
    };

    const aoSair = (e: globalThis.DragEvent) => {
      if (!temArquivos(e.dataTransfer)) return;
      profundidade.current -= 1;
      if (profundidade.current <= 0) setArrastando(false);
    };

    const aoSoltar = (e: globalThis.DragEvent) => {
      if (!temArquivos(e.dataTransfer)) return;
      e.preventDefault();
      profundidade.current = 0;
      setArrastando(false);
      adicionar(arquivosDeTransferencia(e.dataTransfer));
    };

    document.addEventListener("paste", aoColar);
    window.addEventListener("dragenter", aoEntrar);
    window.addEventListener("dragover", aoPassar);
    window.addEventListener("dragleave", aoSair);
    window.addEventListener("drop", aoSoltar);

    return () => {
      document.removeEventListener("paste", aoColar);
      window.removeEventListener("dragenter", aoEntrar);
      window.removeEventListener("dragover", aoPassar);
      window.removeEventListener("dragleave", aoSair);
      window.removeEventListener("drop", aoSoltar);
    };
  }, [adicionar]);

  return { anexos, adicionar, remover, limpar, erro, setErro, arrastando };
};
